import React, { useRef, useState } from "react";
import { CloudAdd24Filled, CloudAdd24Regular } from "@fluentui/react-icons";
import { useToast } from "../../ToastProvider";
import { FileReference } from "../OiReport";
import { useAnalysisOrder } from "../redux/ReduxContext";

interface AnalysisOrderFilesProps {
    onOpenFileGallery?: (files: FileReference[], initialIndex: number) => void;
}

export default function AnalysisOrderFiles({ onOpenFileGallery }: AnalysisOrderFilesProps) {
    const [isUploading, setIsUploading] = useState(false);
    const [isDragOver, setIsDragOver] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { showToast } = useToast();
    const { state, dispatch } = useAnalysisOrder();

    const processFiles = async (files: FileList) => {
        if (!files || files.length === 0) return;
        setIsUploading(true);

        try {
            const { UploadFile } = await import("../../../services/OiService");
            const uploadResults = await Promise.all(Array.from(files).map(async (file) => {
                try {
                    const fileId = await UploadFile(file);
                    const cleanFileId = fileId ? fileId.toString().replace(/^["']|["']$/g, '') : null;
                    return cleanFileId ? { uuid: cleanFileId, fileName: file.name } : null;
                } catch (error) {
                    console.error(`Failed to upload ${file.name}:`, error);
                    showToast({ title: 'Upload failed', intent: 'error', body: `Failed to upload ${file.name}` });
                    return null;
                }
            }));

            uploadResults.forEach(upload => {
                if (upload) {
                    dispatch({ type: 'ADD_ARRAY_ITEM', payload: { field: 'uploadedFiles', item: upload } });
                }
            });
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    return (
        <div
            className="relative p-4 bg-white dark:bg-oi-dark-background transition-all duration-200 drop-shadow-md dark:drop-shadow-none text-primary-dark dark:text-primary-light"
            onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(true); }}
            onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(false); }}
            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
            onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsDragOver(false);
                if (e.dataTransfer.files?.length) {
                    processFiles(e.dataTransfer.files);
                }
            }}
        >
            <div className="flex items-center gap-2">
                <h6 className="mb-2 text-sm font-bold">Attach file</h6>
            </div>

            {isDragOver && (
                <div className="absolute inset-0 bg-blue-100/80 flex items-center justify-center pointer-events-none rounded-md">
                    <div className="text-center">
                        <CloudAdd24Regular className="mx-auto mb-2 text-4xl text-blue-600" />
                        <p className="text-lg font-medium text-blue-700">Drop files here to upload</p>
                    </div>
                </div>
            )}

            <input ref={fileInputRef} type="file" multiple accept="*/*" onChange={(event) => event.target.files && processFiles(event.target.files)} style={{ display: 'none' }} />

            {state.uploadedFiles.length > 0 && (
                <div className="max-h-40 overflow-y-auto my-2">
                    {state.uploadedFiles.map((fileItem, index) => (
                        <div key={`analysis-file-${index}`} className="flex items-center gap-2 hover:bg-primary-dark/10 dark:hover:bg-primary-light/10 p-2 rounded transition-colors">
                            <input
                                type="checkbox"
                                checked
                                aria-label={`Remove file ${fileItem.fileName}`}
                                onChange={() => dispatch({ type: 'DELETE_ARRAY_ITEM', payload: { field: 'uploadedFiles', index } })}
                                className="w-4 h-4"
                            />
                            <button onClick={() => onOpenFileGallery?.(state.uploadedFiles, index)} className="text-sm text-oi-button-blue dark:text-blue-300 underline bg-transparent border-none cursor-pointer p-0 text-left">{fileItem.fileName}</button>
                        </div>
                    ))}
                </div>
            )}

            {!isUploading && (
                <div className="text-center py-6 text-primary-dark/70 dark:text-primary-light border-2 border-dashed border-primary-dark/40 dark:border-primary-light rounded-lg my-4 cursor-pointer" onClick={() => fileInputRef.current?.click()}>
                    <CloudAdd24Filled className="mx-auto mb-3 text-3xl opacity-50 w-12 h-12" />
                    <p className="text-xs">Click or drag and drop files here</p>
                </div>
            )}
        </div>
    );
}
